import { z } from 'zod';
// ============================================================
// Studio Chatbot — 사용자 문의 챗봇
// ============================================================
/** Anthropic Messages API 호환 content block */
export const ChatbotContentBlockSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string() }),
    z.object({
        type: z.literal('tool_use'),
        id: z.string(),
        name: z.string(),
        input: z.record(z.string(), z.unknown()),
    }),
    z.object({
        type: z.literal('tool_result'),
        tool_use_id: z.string(),
        content: z.string(),
        is_error: z.boolean().optional(),
    }),
]);
export const ChatbotMessageRoleSchema = z.enum(['user', 'assistant']);
// 클라이언트 → 서버 메시지 전송
export const SendChatbotMessageRequestSchema = z.object({
    sessionId: z.string().cuid2().nullable(),
    message: z.string().min(1, '메시지를 입력하세요').max(2000, '메시지는 2000자를 초과할 수 없습니다'),
});
// FAQ 칩 — 위젯이 보여줄 공개 FAQ 목록 (질문만; 답변은 서버가 보관)
export const ChatbotFaqChipSchema = z.object({
    id: z.string(),
    category: z.string(),
    question: z.string(),
});
// FAQ 칩 탭 — LLM 우회. faqId 만 보내면 서버가 저장된 질문·답변을 즉시 반환한다.
// 정해진 질문엔 정해진 답변 → provider 과부하(overloaded_error) 영향 없음, 비용 0.
export const SendChatbotFaqRequestSchema = z.object({
    sessionId: z.string().cuid2().nullable(),
    faqId: z.string().min(1),
});
export const SendChatbotFaqResponseSchema = z.object({
    sessionId: z.string(),
    question: z.string(),
    answer: z.string(),
});
// 클라이언트가 받는 메시지(휘발성 표시용 — 텍스트 + tool_use 메타만)
export const ChatbotDisplayMessageSchema = z.object({
    id: z.string(),
    role: ChatbotMessageRoleSchema,
    text: z.string(),
    toolCalls: z
        .array(z.object({ name: z.string(), summary: z.string().optional() }))
        .optional(),
    createdAt: z.string(),
});
// 에스컬레이션 요청
export const EscalateChatbotSessionRequestSchema = z.object({
    name: z.string().min(1).max(50),
    email: z.string().email().max(255),
    category: z.enum(['BUG', 'PAYMENT', 'ACCOUNT', 'FEATURE', 'OTHER']),
    message: z
        .string()
        .min(10, '문의 내용을 10자 이상 입력하세요')
        .max(5000, '문의 내용은 5000자를 초과할 수 없습니다'),
});
export const EscalateChatbotSessionResponseSchema = z.object({
    inquiryId: z.string().cuid2(),
});
// ============================================================
// Live chat (escalate 후 양방향 대화 — 비동기 폴링)
// ============================================================
/** chatbot_messages.role 전체 — LLM 외에 라이브 채팅 역할 포함. */
export const LiveChatMessageRoleSchema = z.enum(['user', 'assistant', 'agent', 'system']);
/** 폴링 응답·송신 응답에서 클라이언트가 보는 단일 메시지 모양. */
export const LiveChatMessageSchema = z.object({
    id: z.string(),
    role: LiveChatMessageRoleSchema,
    text: z.string(),
    createdAt: z.string(),
});
/** 사용자가 라이브 채팅에서 새 메시지 송신 — '!종료' 입력 시 세션이 종료된다. */
export const SendLiveChatMessageRequestSchema = z.object({
    content: z
        .string()
        .min(1, '메시지를 입력하세요')
        .max(2000, '메시지는 2000자를 초과할 수 없습니다'),
});
/** 종료 명령. */
export const LIVE_CHAT_END_COMMAND = '!종료';
export const SendLiveChatMessageResponseSchema = z.object({
    /** !종료 명령이었으면 null — 메시지로 저장되지 않는다. */
    message: LiveChatMessageSchema.nullable(),
    /** 이번 호출로 세션이 종료되었는지. */
    ended: z.boolean(),
});
export const LiveChatPollResponseSchema = z.object({
    messages: z.array(LiveChatMessageSchema),
    agentJoinedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
});
// ============================================================
// 사용자 세션 목록·복원 — 위젯이 이전 대화방을 다시 열어보기 위해 사용
// ============================================================
/** 위젯의 세션 목록에서 한 줄로 보여줄 카드 정보. */
export const ChatbotSessionListItemSchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    agentJoinedAt: z.string().nullable(),
    escalatedInquiryId: z.string().nullable(),
    messageCount: z.number().int(),
    /** 첫 사용자 메시지 일부 — 카드 제목으로 사용. 없으면 빈 문자열. */
    firstUserMessage: z.string(),
    /** 마지막 메시지(role 무관) 일부 — 미리보기 한 줄. */
    lastMessagePreview: z.string(),
});
export const ChatbotSessionListResponseSchema = z.object({
    sessions: z.array(ChatbotSessionListItemSchema),
});
/** 한 세션의 전체 메시지 + 라이브 모드 판단에 필요한 메타. */
export const ChatbotSessionDetailSchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    agentJoinedAt: z.string().nullable(),
    escalatedInquiryId: z.string().nullable(),
    messages: z.array(LiveChatMessageSchema),
});
// ============================================================
// Admin: FAQ CRUD
// ============================================================
export const ChatbotFaqSchema = z.object({
    id: z.string().cuid2(),
    category: z.string().min(1).max(64),
    question: z.string().min(1),
    answer: z.string().min(1),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    updatedBy: z.string().cuid2().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateChatbotFaqRequestSchema = z.object({
    category: z.string().min(1).max(64),
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(5000),
    sortOrder: z.number().int().default(0),
    isActive: z.boolean().default(true),
});
export const UpdateChatbotFaqRequestSchema = CreateChatbotFaqRequestSchema.partial();
// ============================================================
// Admin: Session 조회
// ============================================================
export const ChatbotSessionSummarySchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().cuid2(),
    userEmail: z.string().nullable(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    escalatedInquiryId: z.string().nullable(),
    messageCount: z.number().int(),
    totalInputTokens: z.number().int(),
    totalOutputTokens: z.number().int(),
    totalCacheReadTokens: z.number().int(),
    totalCacheWriteTokens: z.number().int(),
});
//# sourceMappingURL=chatbot.js.map